import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ShieldCheck, Check, X, LogOut } from "lucide-react";
import { useUser } from "@/context/UserContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const RULES = [
  { key: "len", label: "At least 12 characters", test: (p) => p.length >= 12 },
  { key: "upper", label: "One uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { key: "lower", label: "One lowercase letter", test: (p) => /[a-z]/.test(p) },
  { key: "num", label: "One number", test: (p) => /[0-9]/.test(p) },
  { key: "special", label: "One special character", test: (p) => /[^A-Za-z0-9]/.test(p) },
];

function formatError(detail, fallback) {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ") || fallback;
  return fallback;
}

export default function ChangePassword() {
  const { currentUser, changePassword, logout } = useUser();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const forced = !!currentUser?.must_reset;
  const rulesPass = RULES.every((r) => r.test(next));
  const differs = next.length > 0 && next !== current;
  const matches = next.length > 0 && next === confirm;
  const canSubmit = current.length > 0 && rulesPass && differs && matches && !busy;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!differs) return setError("New password must be different from your current password");
    if (!matches) return setError("New password and confirmation do not match");
    setBusy(true);
    try {
      await changePassword(current, next, confirm);
      navigate("/", { replace: true });
    } catch (err) {
      setError(formatError(err.response?.data?.detail, "Unable to change password. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const handleSignOut = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0B1E5B] px-4 py-10">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="mb-6 flex flex-col items-center text-center">
          <img src="/edju-logo.jpeg" alt="Edju Cat" className="mb-4 h-12 w-12 rounded-xl ring-1 ring-[#1D4ED8]/50" />
          <h1 className="font-heading text-2xl font-semibold text-white">
            {forced ? "Set a New Password" : "Change Password"}
          </h1>
          {forced && (
            <p className="mt-2 max-w-sm text-sm text-[#F8F7F4]/70">
              For your security, please choose a new password before continuing.
            </p>
          )}
        </div>

        <form
          onSubmit={submit}
          data-testid="change-password-form"
          className="rounded-2xl border border-white/10 bg-white/[0.04] p-7 backdrop-blur-xl"
        >
          <div className="mb-6 flex items-center gap-2 text-sm text-[#F8F7F4]/70">
            <ShieldCheck size={15} className="text-[#1D4ED8]" />
            <span>{currentUser?.email}</span>
          </div>

          <div className="space-y-4">
            <Field id="current" label={forced ? "Temporary password" : "Current password"} value={current} onChange={setCurrent} testid="current-password-input" />
            <Field id="next" label="New password" value={next} onChange={setNext} testid="new-password-input" />
            <Field id="confirm" label="Confirm new password" value={confirm} onChange={setConfirm} testid="confirm-password-input" />
          </div>

          <ul className="mt-4 space-y-1.5" data-testid="password-rules">
            {RULES.map((r) => {
              const ok = r.test(next);
              return (
                <li key={r.key} className={`flex items-center gap-2 text-xs ${ok ? "text-teal-300" : "text-[#F8F7F4]/50"}`}>
                  {ok ? <Check size={13} /> : <X size={13} />} {r.label}
                </li>
              );
            })}
            <li className={`flex items-center gap-2 text-xs ${differs ? "text-teal-300" : "text-[#F8F7F4]/50"}`}>
              {differs ? <Check size={13} /> : <X size={13} />} Different from current password
            </li>
          </ul>

          {error && (
            <p data-testid="change-password-error" className="mt-4 rounded-lg border border-red-400/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">
              {error}
            </p>
          )}

          <Button
            type="submit"
            data-testid="change-password-submit"
            disabled={!canSubmit}
            className="mt-6 w-full bg-[#1D4ED8] font-semibold text-white hover:bg-[#1A45C0]"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : "Update Password"}
          </Button>

          {!busy && (current.length === 0 || !rulesPass || !differs || !matches) && (
            <p data-testid="change-password-hint" className="mt-3 text-center text-xs text-[#F8F7F4]/40">
              Meet all password rules and confirm a matching new password to continue.
            </p>
          )}

          <button
            type="button"
            data-testid="change-password-signout"
            onClick={handleSignOut}
            className="mt-4 flex w-full items-center justify-center gap-2 text-xs text-[#F8F7F4]/50 transition-colors hover:text-white"
          >
            <LogOut size={13} /> Sign out instead
          </button>
        </form>
      </motion.div>
    </div>
  );
}

const Field = ({ id, label, value, onChange, testid }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-xs text-[#F8F7F4]/70">{label}</Label>
    <Input
      id={id}
      type="password"
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      autoComplete="new-password"
      required
      className="border-white/15 bg-white/5 text-white placeholder:text-white/30 focus-visible:ring-[#1D4ED8]"
    />
  </div>
);
